// Client mirror of the agents grants (spec 14 §7.1, D-513 ruling 1). The server
// decides; this only keeps the screens from offering a control that would be
// refused. `tools/ci/web-permission-mirror.ts` holds it to `systemRoleGrants`.
//
// The journal has no code of its own (§7.1): every office role reads it, and
// the server serves each reader the cards it passes §7.2's checks 1 and 3 for.
// So no entry here gates the journal, and `can_approve` / `can_undo` on each
// card, computed by the server from exactly those checks, decide its buttons.

import {
  derivePermissions,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
} from "../../../../lib/role-codes";

/** The five codes 14-A registers (spec 14 §7.1, group_admin 159 → 164). */
export const AGENTS_PERMISSIONS = [
  "agents.read",
  "agents.invoke",
  "agents.manage",
  "agents.config.manage",
  "agents.activity.read",
] as const;

export type AgentsPermission = (typeof AGENTS_PERMISSIONS)[number];

/**
 * Role code to agents codes, as spec 14 §7.1 seeds them. `agents.invoke` is
 * every seed role's except floor_worker's, which holds no agents code at all.
 * `agents.usage.read` and `agents.usage.cost_read` register with 14-F's usage
 * screen and are not here (D-513 ruling 2).
 */
export const AGENTS_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...AGENTS_PERMISSIONS],
  facility_admin: ["agents.read", "agents.invoke", "agents.manage", "agents.activity.read"],
  planner: ["agents.invoke"],
  sales: ["agents.invoke"],
  procurement: ["agents.invoke"],
  finance: ["agents.invoke"],
  qc: ["agents.invoke"],
  maintenance: ["agents.invoke"],
  storekeeper: ["agents.invoke"],
};

/** The effective agents set: the feed's permission list cut to this slice,
 * or the mirror without one (D-586). */
export function deriveAgentsPermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, AGENTS_ROLE_GRANTS);
}

export interface AgentsPermissions extends AreaPermissions {
  /** Reads the Hub with its config controls (`agents.read`). */
  readonly canRead: boolean;
  /**
   * Reads the agents list at all: `agents.read`, or `agents.invoke`, which the
   * server narrows to the reader's own domain's agents, read-only (D-512
   * ruling 8).
   */
  readonly canList: boolean;
  /** Enables, pauses, renames, edits memberships and policy rows (`agents.manage`). */
  readonly canManage: boolean;
  /** Caps, the prompt override and activation, settings, a laxer policy (`agents.config.manage`). */
  readonly canConfig: boolean;
  /** The guardrail history and run detail (`agents.activity.read`). */
  readonly canReadActivity: boolean;
}

export function useAgentsPermissions(): AgentsPermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveAgentsPermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    canRead: codes.has("agents.read"),
    canList: codes.has("agents.read") || codes.has("agents.invoke"),
    canManage: codes.has("agents.manage"),
    canConfig: codes.has("agents.config.manage"),
    canReadActivity: codes.has("agents.activity.read"),
  };
}
